import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent } from '../../../shared/components/ui';
import { buildJobTypeDistributionData, JOB_TYPE_COLORS } from '../../../shared/services/insightsApi';
import { formatCurrencyCompact } from '../../../shared/utils/formatters';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{label}</p>
      <div className="space-y-1">
        <div className="flex justify-between gap-6">
          <span className="text-gray-500 dark:text-gray-400">Projects</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{d?.count}</span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-gray-500 dark:text-gray-400">Avg $/SF</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">${d?.avgCostPerSf?.toFixed(2)}</span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-gray-500 dark:text-gray-400">Total Value</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrencyCompact(d?.totalValue)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * JobTypeDistributionChart — bar chart of project count per job type.
 * Props: estimates (Array)
 */
export function JobTypeDistributionChart({ estimates }) {
  const data = useMemo(() => buildJobTypeDistributionData(estimates), [estimates]);

  return (
    <Card className="shadow-sm h-full">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Project Mix</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Estimate count by building type</p>
          </div>
          {data.length > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
              {data.length} {data.length === 1 ? 'type' : 'types'}
            </span>
          )}
        </div>
      </div>

      <CardContent className="pt-4 pb-2">
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No project data</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add estimates to see project mix</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="jobType"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval={0}
                angle={data.length > 4 ? -30 : 0}
                textAnchor={data.length > 4 ? 'end' : 'middle'}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                width={28}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={48} label={{ position: 'top', fontSize: 11, fill: '#6b7280' }}>
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={JOB_TYPE_COLORS[index % JOB_TYPE_COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default JobTypeDistributionChart;
